import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api, qs } from "@/lib/api";
import { useKeys } from "@/lib/queries";
import { atLeast, type Role, type SendingWorkflow } from "@/lib/types";

/**
 * Tasks (ACL-408, ADR-111 amendment in platform): a to do for a person about a lead, by a time.
 * A workflow's Create task step makes them; nothing in the console does yet. A person reads their
 * own (`GET /tasks`), a supervisor or an admin everybody's (`whose=everyone`), a lead's are on its
 * record (`GET /leads/{id}/tasks`), and the person a task is for, a supervisor or an admin marks it
 * done or open again (`POST /tasks/{id}/done`). The rules here are pure, with tests.
 */

export interface Task {
  id: string;
  lead_id: string;
  lead_name: string | null;
  /** The reader may open the lead: a rep can be given a task about one they cannot, and then the
   *  task names it without a link. */
  lead_readable: boolean;
  title: string;
  due_at: string;
  done_at: string | null;
  done_by_name: string | null;
  assignee_user_id: number | null;
  assignee_name: string | null;
  workflow: SendingWorkflow | null;
  created_at: string;
}

export type TaskWhose = "mine" | "everyone";
export type TaskStatus = "open" | "done" | "all";

/** Tasks are made by the workflows process, which sends no refetch hint: the lists poll. */
const POLL_MS = 60_000;

/** A 404: the core-api behind the console has no tasks yet. */
export function notThere(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/**
 * Whether the core-api behind the console has lead owners and tasks (ACL-408): its card names the
 * lead's owner, null when nobody owns it, and a core-api from before leaves the key out. The
 * record's Tasks tab, the timeline's Owner chip and the opportunity dialog's Tasks section show
 * only then, so a console that reaches a server first asks nothing that core-api does not have.
 */
export function ownersKnown(lead: { owner?: unknown } | null | undefined): boolean {
  return lead != null && lead.owner !== undefined;
}

/** Whether `user` may mark `task` done or open it again: the person it is for, a supervisor or an
 *  admin, as core-api allows. Anyone else sees the round box, but cannot press it. */
export function canMarkTask(
  task: Pick<Task, "assignee_user_id">,
  user: { id: number; role: Role } | null | undefined,
): boolean {
  if (!user) return false;
  return atLeast(user.role, "supervisor") || task.assignee_user_id === user.id;
}

/**
 * Whether core-api has `GET /tasks`, for the sidebar's Tasks link: asked once a session. Undefined
 * until it answers; false on a 404, so the link stays hidden until the console is loaded again.
 */
export function useTasksAvailable(): boolean | undefined {
  const keys = useKeys();
  const probe = useQuery({
    queryKey: keys.scoped(["tasks", "available"]),
    queryFn: async () => {
      try {
        await api.get<Task[]>(`/tasks${qs({ whose: "mine", status: "open" })}`);
        return true;
      } catch (e) {
        if (notThere(e)) return false;
        throw e;
      }
    },
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  // Another failure is not a missing route: the link stays, and the page says what went wrong.
  return probe.data ?? (probe.isError ? true : undefined);
}

export function useTasks(whose: TaskWhose, status: TaskStatus) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["tasks", "list", whose, status]),
    queryFn: () => api.get<Task[]>(`/tasks${qs({ whose, status })}`),
    // A core-api with no tasks is asked once, not every minute.
    retry: (count, error) => !notThere(error) && count < 3,
    refetchInterval: (query) => (notThere(query.state.error) ? false : POLL_MS),
  });
}

export function useLeadTasks(leadId: string | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["tasks", "lead", leadId ?? "none"]),
    queryFn: () => api.get<Task[]>(`/leads/${leadId}/tasks`),
    enabled: Boolean(leadId),
    refetchInterval: POLL_MS,
  });
}

/** Mark a task done, or open again; every task list is read again after. */
export function useMarkTask() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, done }: { id: string; done: boolean }) => api.post<Task>(`/tasks/${id}/done`, { done }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.scoped(["tasks"]) }),
  });
}

/** Whether an open task is past its due time. */
export function overdue(task: Pick<Task, "due_at" | "done_at">, now: number): boolean {
  return task.done_at === null && Date.parse(task.due_at) < now;
}

/**
 * When a task is due, as its row says it, from `now`: "Due now", "Due in 15 min", "Due in 3 h",
 * "Due in 2 days", "Overdue by 20 min"; a done one says when it was done instead.
 */
export function dueText(task: Pick<Task, "due_at" | "done_at">, now: number): string {
  if (task.done_at !== null) return "Done";
  const diff = Date.parse(task.due_at) - now;
  const span = spanText(Math.abs(diff));
  if (Math.abs(diff) < 60_000) return "Due now";
  return diff > 0 ? `Due in ${span}` : `Overdue by ${span}`;
}

function spanText(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h`;
  return `${Math.round(hours / 24)} days`;
}

/** Open ones first, the most overdue at the top; then the done ones, the newest first. */
export function sortTasks(tasks: readonly Task[]): Task[] {
  return [...tasks].sort((a, b) => {
    if ((a.done_at === null) !== (b.done_at === null)) return a.done_at === null ? -1 : 1;
    if (a.done_at === null) return a.due_at.localeCompare(b.due_at) || a.id.localeCompare(b.id);
    return (b.done_at ?? "").localeCompare(a.done_at ?? "") || a.id.localeCompare(b.id);
  });
}
